/**
 * The app's date layer.
 *
 * Public surface is `Temporal.*` and `string` — never `Dayjs`, never `dayjs.ConfigType`. That
 * is the property worth enforcing: a layer whose types are the library it wraps is a re-export,
 * and the underlying value's timezone anchor leaks back out to every call site.
 *
 * Split by the two kinds of value, because conflating them is the bug class:
 *
 * - **Calendar date** — a report boundary, a day picker. `Temporal.PlainDate`, wire format
 *   `YYYY-MM-DD`. Has no instant and must not be given one.
 * - **Timestamp** — a scheduled publish time, a `created_at`. `Temporal.Instant`, wire format
 *   a UTC ISO string, displayed as wall-clock in the profile timezone.
 *
 * Calendar-date helpers and range formatting come from dashing-ui
 * (`@dashhudson/dashing-ui/utils/temporal`, `.../utils/formatters`) so the app and the date
 * components agree by construction. Timestamps are ours — see `./timestamp` and `./format`.
 */
import { temporalToday } from '@dashhudson/dashing-ui/utils/temporal';

import { guessTimezone, getDateComponentTimezone } from '@/utils/timezone';

import { formatInstant } from './format';

export * from './format';
export * from './timestamp';

/**
 * The timezone every date in the app should resolve in.
 *
 * While the `dashingUiTimezone` flag is off this is the browser's zone, which is what the date
 * components and the existing call sites already use — so routing a call site through this layer
 * is a no-op until the flag flips. Resolving it here rather than at each call site is the point:
 * the app and dashing-ui's components must agree on the anchor, and disagreeing in *either*
 * direction moves the day.
 *
 * @see getDateComponentTimezone
 */
export function resolveTimezone(): string {
  return getDateComponentTimezone() ?? guessTimezone() ?? 'UTC';
}

/**
 * Format a timestamp for display in the timezone the app resolves to.
 *
 * The zone-defaulting wrapper over {@link formatInstant}: a publish time, a `created_at`, an
 * upload time. Reading these browser-local is what shows someone in Tokyo the Tokyo hour for a
 * post their Halifax brand publishes at a Halifax one.
 */
export function formatTimestamp(value: Date | string | null | undefined, pattern: string): string {
  return formatInstant(value, pattern, resolveTimezone());
}

/**
 * Today as a calendar date in the zone the date components use — the Temporal `minDate`/`maxDate`
 * for a picker. Call it per render rather than caching it, so a tab left open overnight moves on.
 */
export function todayPlainDate(): Temporal.PlainDate {
  return temporalToday(resolveTimezone());
}

/** {@link todayPlainDate} minus one day: the latest complete day of analytics data. */
export function yesterdayPlainDate(): Temporal.PlainDate {
  return todayPlainDate().subtract({ days: 1 });
}
